import React from 'react';
import { PageHero, BottomCTA } from '../components/PageHelpers';
import { Sec, SH } from '../components/Section';
import { Btn, Crd } from '../components/Button';
import { MockupGraphic, DashboardMockup } from '../components/MockupGraphic';
import { Orbs, Grid } from '../components/Orbs';
import { AnimNum, BulletList } from '../components/AnimatedComponents';
import { IntelligenceIcon, ConsultationIcon, ArtefactsIcon } from '../components/Icons3D';
import { ScoringWidget, PlatformFeatures, PricingWidget, TestimonialsWidget, FAQWidget, SlidrWidget } from '../widgets';
import { FragmentedIntelAnimation } from '../components/FragmentedIntelAnimation';
import { LogoScroll } from '../components/LogoScroll';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { L } from '../router/Router';
import { motion } from 'framer-motion';

export const HomePage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="home-page">
      {/* HERO SECTION - Exact original content */}
      <PageHero
        eyebrow="DECK INTELLIGENCE PLATFORM"
        title="Transform Your Deck"
        accent="to DecX"
        subtitle="The power that drives closure."
        body="Score. Insights. Artefacts. Strategy — everything to get the yes. Whether you're raising capital, closing deals, or winning approvals — your deck is the gatekeeper to every yes."
        cta="Get Free DecX Score"
        ctaLink="/score"
        cta2="Explore Platform"
        cta2Link="/platform"
        stats={[
          { value: <AnimNum target={1000} suffix="+" />, label: 'Decks Analyzed' },
          { value: <AnimNum target={500} prefix="$" suffix="M+" />, label: 'Raised by Users' },
          { value: <AnimNum target={5} />, label: 'Dimensions Scored' }
        ]}
      >
        <DashboardMockup />
      </PageHero>

      {/* LOGO SCROLL SECTION - Social proof */}
      <LogoScroll />

      {/* THE PROBLEM SECTION - Exact original content */}
      <Sec id="problem" dark={theme === 'light'}>
        <Orbs />
        <Grid o={0.02} />
        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SH
            eyebrow="THE PROBLEM"
            title="Your Deck Gets One Shot."
            accent="Most Miss It."
            center
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              fontSize: '18px',
              lineHeight: 1.7,
              opacity: 0.85,
              textAlign: 'center',
              maxWidth: '700px',
              margin: '0 auto 40px'
            }}
          >
            Investors decide in 90 seconds. Buyers forget your pitch by lunch. Executives fund the clearest story — not the best product.
          </motion.p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '32px' }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <BulletList
                items={[
                  'No score to measure effectiveness',
                  'No insights into what the audience needs',
                  'No benchmarks against what works',
                  'No artefacts to support the close',
                  'No system — just hope'
                ]}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Crd glow style={{ textAlign: 'center', padding: '32px' }}>
                <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', opacity: 0.9 }}>
                  The problem isn't your business.
                </p>
                <p style={{ fontSize: '20px', fontWeight: 700, color: C.secondary }}>
                  It's fragmented intelligence.
                </p>
              </Crd>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '48px' }}
          >
            <FragmentedIntelAnimation />
          </motion.div>
        </div>
      </Sec>

      {/* THE SOLUTION SECTION - Exact original content */}
      <Sec id="solution" dark={theme === 'light'}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SH
            eyebrow="THE SOLUTION"
            title="From Deck to DecX"
            accent="Everything You Need to Close"
            subtitle="One platform. Complete intelligence. Ready artefacts."
            center
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {/* Intelligence Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
              viewport={{ once: true }}
            >
              <Crd glow style={{ height: '100%' }} data-testid="solution-intelligence">
                <div style={{ marginBottom: '20px' }}>
                  <IntelligenceIcon size={56} />
                </div>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: C.secondary, marginBottom: '12px' }}>
                  INTELLIGENCE
                </div>
                <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', fontFamily: "'Sansation', sans-serif" }}>
                  Know Where You Stand
                </h4>
                <BulletList
                  items={[
                    'DecX Score & Deep Analysis',
                    'Unexplored Intelligence',
                    'Key Audience Insights',
                    'Industry Trend Reports'
                  ]}
                  small
                />
                <p style={{ fontSize: '13px', fontStyle: 'italic', opacity: 0.7, marginTop: '16px' }}>
                  Stop guessing. Start knowing.
                </p>
              </Crd>
            </motion.div>

            {/* Consultation Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Crd glow style={{ height: '100%' }} data-testid="solution-consultation">
                <div style={{ marginBottom: '20px' }}>
                  <ConsultationIcon size={56} />
                </div>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: C.secondary, marginBottom: '12px' }}>
                  CONSULTATION
                </div>
                <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', fontFamily: "'Sansation', sans-serif" }}>
                  Expert Guidance On Demand
                </h4>
                <BulletList
                  items={[
                    '1:1 Strategy Calls',
                    'Positioning Review',
                    'Narrative Architecture',
                    'Go-to-Market Alignment'
                  ]}
                  small
                />
                <p style={{ fontSize: '13px', fontStyle: 'italic', opacity: 0.7, marginTop: '16px' }}>
                  18+ years of pattern recognition.
                </p>
              </Crd>
            </motion.div>

            {/* Artefacts Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Crd glow style={{ height: '100%' }} data-testid="solution-artefacts">
                <div style={{ marginBottom: '20px' }}>
                  <ArtefactsIcon size={56} />
                </div>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: C.secondary, marginBottom: '12px' }}>
                  ARTEFACTS
                </div>
                <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', fontFamily: "'Sansation', sans-serif" }}>
                  Everything to Close
                </h4>
                <BulletList
                  items={[
                    'Pitch Canvas',
                    'Deck Rebuild Prompts',
                    'Ready Documents',
                    'Outreach Sequences',
                    'Brand Videos'
                  ]}
                  small
                />
                <p style={{ fontSize: '13px', fontStyle: 'italic', opacity: 0.7, marginTop: '16px' }}>
                  From insight to action in minutes.
                </p>
              </Crd>
            </motion.div>
          </div>
        </div>
      </Sec>

      {/* HOW IT WORKS SECTION - Exact original content */}
      <Sec id="how-it-works" dark={theme === 'light'}>
        <Grid o={0.02} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SH
            eyebrow="HOW IT WORKS"
            title="From Upload to Close-Ready"
            accent="In 4 Steps"
            center
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {[
              { num: '01', title: 'UPLOAD', desc: 'Drop your deck — PDF, PPTX, or Google Slides. 100% confidential.' },
              { num: '02', title: 'SCORE', desc: 'Our engine analyzes across 5 dimensions. Score in 24 hours. Free.' },
              { num: '03', title: 'GO DEEPER', desc: 'Paid version unlock intelligence, insights, and artefacts. Everything.' },
              { num: '04', title: 'CLOSE', desc: "Transform your deck from interesting to let's do this." }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
              >
                <Crd data-testid={`step-card-${i}`}>
                  <div
                    style={{
                      fontSize: '56px',
                      fontWeight: 700,
                      background: C.grad,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      marginBottom: '16px',
                      lineHeight: 1
                    }}
                  >
                    {step.num}
                  </div>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '12px', letterSpacing: '0.1em', color: C.secondary }}>
                    {step.title}
                  </h4>
                  <p style={{ fontSize: '14px', lineHeight: 1.6, opacity: 0.7 }}>
                    {step.desc}
                  </p>
                </Crd>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Btn to="/score">Start Free — Get Your DecX Score</Btn>
          </div>
        </div>
      </Sec>

      <ScoringWidget />
      <PlatformFeatures />

      {/* USE CASES PREVIEW - Exact original content */}
      <Sec id="use-cases-preview" dark={theme === 'light'}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SH
            eyebrow="WHO IT'S FOR"
            title="One Platform."
            accent="Every Deck That Matters."
            center
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {[
              {
                i: '🚀',
                t: 'Founders Raising Capital',
                s: 'Pitch Decks for Investors',
                d: "Get your DecX Score, fix what's broken, close your round faster.",
                to: '/for-founders'
              },
              {
                i: '💼',
                t: 'B2B Sales Teams',
                s: 'Sales Decks for Buyers',
                d: 'Transform your sales deck into a conversion machine.',
                to: '/for-sales-teams'
              },
              {
                i: '📋',
                t: 'Product Managers',
                s: 'Product Decks for Stakeholders',
                d: 'Get budget approvals faster with decks that land.',
                to: '/for-product-managers'
              },
              {
                i: '🤝',
                t: 'Accelerators & VCs',
                s: 'Portfolio Deck Scoring',
                d: 'Score entire cohorts. White-label reports. API access.',
                to: '/for-accelerators'
              }
            ].map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <L to={uc.to}>
                  <Crd glow style={{ cursor: 'pointer', height: '100%' }} data-testid={`use-case-${i}`}>
                    <div style={{ fontSize: '32px', marginBottom: '14px' }}>{uc.i}</div>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: C.secondary, marginBottom: '8px', opacity: 0.8 }}>
                      {uc.s}
                    </div>
                    <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px', fontFamily: "'Sansation', sans-serif", color: C.white }}>
                      {uc.t}
                    </h4>
                    <p style={{ fontSize: '14px', lineHeight: 1.6, opacity: 0.7, marginBottom: '16px' }}>
                      {uc.d}
                    </p>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: C.secondary }}>Learn More →</span>
                  </Crd>
                </L>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Btn variant="outline" to="/use-cases">View All Use Cases</Btn>
          </div>
        </div>
      </Sec>

      <PricingWidget />
      <SlidrWidget />
      <TestimonialsWidget />
      <FAQWidget />

      <BottomCTA
        title="Ready to Transform Your Deck"
        accent="to DecX?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free DecX Score"
        ctaLink="/score"
        cta2Text="Explore Platform"
        cta2Link="/platform"
      />
    </div>
  );
};
